import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { 
  ArrowUpRight, Check, X, ChevronDown, Clock, 
  CheckCircle, XCircle, AlertTriangle
} from 'lucide-react';
import AdminLayout from '../components/AdminLayout';
import { getAllWithdrawals, updateWithdrawal } from '../services/api';
import { toast } from 'sonner';

const AdminWithdrawalsPage = () => {
  const [withdrawals, setWithdrawals] = useState([]);
  const [loading, setLoading] = useState(true);
  const [statusFilter, setStatusFilter] = useState('pending');
  const [selectedWithdrawal, setSelectedWithdrawal] = useState(null);
  const [adminNotes, setAdminNotes] = useState('');

  useEffect(() => {
    fetchWithdrawals();
  }, [statusFilter]);

  const fetchWithdrawals = async () => {
    try {
      const response = await getAllWithdrawals(statusFilter || undefined);
      setWithdrawals(response.data);
    } catch (error) {
      toast.error('Failed to load withdrawals');
    } finally {
      setLoading(false);
    }
  };

  const handleApprove = async (withdrawalId) => {
    try {
      await updateWithdrawal(withdrawalId, { status: 'approved', admin_notes: adminNotes });
      toast.success('Withdrawal approved and balance deducted');
      setSelectedWithdrawal(null);
      setAdminNotes('');
      fetchWithdrawals();
    } catch (error) {
      toast.error('Failed to approve withdrawal');
    }
  };

  const handleReject = async (withdrawalId) => {
    try {
      await updateWithdrawal(withdrawalId, { status: 'rejected', admin_notes: adminNotes });
      toast.success('Withdrawal rejected');
      setSelectedWithdrawal(null);
      setAdminNotes('');
      fetchWithdrawals();
    } catch (error) {
      toast.error('Failed to reject withdrawal');
    }
  };

  const getStatusBadge = (status) => {
    switch (status) {
      case 'completed':
      case 'approved':
        return <span className="badge badge-success"><CheckCircle className="w-3 h-3 mr-1" />Completed</span>;
      case 'rejected':
        return <span className="badge badge-danger"><XCircle className="w-3 h-3 mr-1" />Rejected</span>;
      default:
        return <span className="badge badge-warning"><Clock className="w-3 h-3 mr-1" />Pending</span>;
    }
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD'
    }).format(amount || 0);
  };

  return (
    <AdminLayout>
      <div data-testid="admin-withdrawals-page">
        {/* Header */}
        <div className="admin-page-header mb-8">
          <h1 className="text-2xl sm:text-3xl font-bold font-['Outfit'] text-white">
            Withdrawal Management
          </h1>
          <p className="text-slate-400">Review and process user withdrawals</p>
        </div>

        {/* Filters */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="admin-filters"
        >
          <div className="filter-select relative">
            <select
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value)}
              className="input-field pr-10 appearance-none cursor-pointer w-full"
              data-testid="filter-withdrawals"
            >
              <option value="">All Status</option>
              <option value="pending">Pending</option>
              <option value="completed">Completed</option>
              <option value="rejected">Rejected</option>
            </select>
            <ChevronDown className="absolute right-4 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400 pointer-events-none" />
          </div>
        </motion.div>

        {/* Withdrawals Table */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.1 }}
          className="glass-card overflow-hidden"
        >
          <div className="overflow-x-auto">
            <table className="data-table">
              <thead>
                <tr>
                  <th>User</th>
                  <th>Amount</th>
                  <th>Crypto</th>
                  <th>Wallet Address</th>
                  <th>Status</th>
                  <th>Date</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {loading ? (
                  [...Array(5)].map((_, i) => (
                    <tr key={i}>
                      <td colSpan="7"><div className="skeleton h-12" /></td>
                    </tr>
                  ))
                ) : withdrawals.length > 0 ? (
                  withdrawals.map((withdrawal) => (
                    <tr key={withdrawal.id} data-testid={`withdrawal-row-${withdrawal.id}`}>
                      <td>
                        <div>
                          <p className="text-white font-medium">{withdrawal.user?.name}</p>
                          <p className="text-slate-400 text-sm">{withdrawal.user?.email}</p>
                        </div>
                      </td>
                      <td>
                        <p className="text-white font-semibold text-lg">{formatCurrency(withdrawal.amount)}</p>
                      </td>
                      <td>
                        <span className="badge badge-info">{withdrawal.crypto_type}</span>
                      </td>
                      <td>
                        <p className="text-slate-400 text-xs font-mono max-w-[150px] truncate" title={withdrawal.wallet_address}>
                          {withdrawal.wallet_address}
                        </p>
                      </td>
                      <td>{getStatusBadge(withdrawal.status)}</td>
                      <td className="text-slate-400">{formatDate(withdrawal.created_at)}</td>
                      <td>
                        {withdrawal.status === 'pending' && (
                          <div className="flex items-center gap-2">
                            <button
                              onClick={() => setSelectedWithdrawal(withdrawal)}
                              className="p-2 rounded-lg bg-emerald-500/10 text-emerald-400 hover:bg-emerald-500/20 transition-colors"
                              title="Review"
                              data-testid={`review-withdrawal-${withdrawal.id}`}
                            >
                              <Check className="w-4 h-4" />
                            </button>
                          </div>
                        )}
                        {withdrawal.admin_notes && (
                          <p className="text-xs text-slate-500 mt-1">{withdrawal.admin_notes}</p>
                        )}
                      </td>
                    </tr>
                  ))
                ) : (
                  <tr>
                    <td colSpan="7" className="text-center py-12">
                      <ArrowUpRight className="w-12 h-12 mx-auto mb-3 text-slate-600" />
                      <p className="text-slate-400">No withdrawals found</p>
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </motion.div>
      </div>

      {/* Review Withdrawal Modal */}
      <AnimatePresence>
        {selectedWithdrawal && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-50 flex items-center justify-center p-4 modal-overlay"
            onClick={() => setSelectedWithdrawal(null)}
          >
            <motion.div
              initial={{ scale: 0.9, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.9, opacity: 0 }}
              className="glass-card p-8 max-w-md w-full"
              onClick={(e) => e.stopPropagation()}
              data-testid="review-withdrawal-modal"
            >
              <h2 className="text-2xl font-bold text-white mb-6">Review Withdrawal</h2>
              
              <div className="space-y-4 mb-6">
                <div className="p-4 bg-white/5 rounded-xl">
                  <p className="text-slate-400 text-sm">User</p>
                  <p className="text-white font-semibold">{selectedWithdrawal.user?.name}</p>
                  <p className="text-slate-400 text-sm">{selectedWithdrawal.user?.email}</p>
                </div>
                <div className="p-4 bg-white/5 rounded-xl">
                  <p className="text-slate-400 text-sm">Amount</p>
                  <p className="text-2xl font-bold text-orange-400">{formatCurrency(selectedWithdrawal.amount)}</p>
                </div>
                <div className="p-4 bg-white/5 rounded-xl">
                  <p className="text-slate-400 text-sm">Cryptocurrency</p>
                  <p className="text-white font-semibold">{selectedWithdrawal.crypto_type}</p>
                </div>
                <div className="p-4 bg-white/5 rounded-xl">
                  <p className="text-slate-400 text-sm">Wallet Address</p>
                  <p className="text-white font-mono text-sm break-all">{selectedWithdrawal.wallet_address}</p>
                </div>
                <div className="p-4 bg-orange-500/10 rounded-xl border border-orange-500/20 flex items-start gap-3">
                  <AlertTriangle className="w-5 h-5 text-orange-400 flex-shrink-0 mt-0.5" />
                  <p className="text-orange-300 text-sm">
                    Approving will deduct {formatCurrency(selectedWithdrawal.amount)} from user's balance.
                  </p>
                </div>
              </div>

              <div className="mb-6">
                <label className="block text-sm font-medium text-slate-300 mb-2">Admin Notes (Optional)</label>
                <input
                  type="text"
                  value={adminNotes}
                  onChange={(e) => setAdminNotes(e.target.value)}
                  className="input-field"
                  placeholder="Add a note..."
                />
              </div>

              <div className="flex gap-4">
                <button
                  onClick={() => handleReject(selectedWithdrawal.id)}
                  className="btn-secondary flex-1 flex items-center justify-center gap-2 text-red-400 border-red-500/20"
                  data-testid="reject-withdrawal"
                >
                  <X className="w-4 h-4" />
                  Reject
                </button>
                <button
                  onClick={() => handleApprove(selectedWithdrawal.id)}
                  className="btn-primary flex-1 flex items-center justify-center gap-2"
                  data-testid="approve-withdrawal"
                >
                  <Check className="w-4 h-4" />
                  Approve
                </button>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </AdminLayout>
  );
};

export default AdminWithdrawalsPage;
